import { useCallback, useMemo, useRef } from 'react';
import {
  type GridControlledStateReasonLookup,
  type GridFilterItem,
  GridLogicOperator,
  gridFilterableColumnDefinitionsSelector,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {
  addFilterGroup,
  addFilterRow,
  type IssueFilterModel,
  listFilterPanelNodes,
  removeFilterRow,
  setFilterGroupLogicOperator,
  setFilterLogicOperator,
  simplifyFilterGroups,
  upsertFilterRow,
} from 'store/issue/models/filterModel/filterGroups';
import { isDefinedValue } from 'shared/utils/value';
import type { UseFilterGroupsResult } from './types';

interface FilterableColumn {
  field: string;
  filterOperators?: readonly { value: string }[];
}

/** A new row on the first filterable column with its first operator, as MUI's panel adds it. */
export function createFilterRow(filterableColumns: FilterableColumn[]): GridFilterItem | undefined {
  const column = filterableColumns.find(({ filterOperators }) => filterOperators?.length);
  const [firstOperator] = column?.filterOperators ?? [];
  return column && firstOperator
    ? { id: crypto.randomUUID(), field: column.field, operator: firstOperator.value }
    : undefined;
}

/** The model with a row to start with when it has none; editing that row puts it into the model. */
export function withStartingRow(model: IssueFilterModel, startingRow: GridFilterItem | undefined): IssueFilterModel {
  if (model.items.length || !startingRow) {
    return model;
  }
  return { ...model, items: [startingRow] };
}

/**
 * The issue grid's filter rows and groups and the changes the panel makes to
 * them, applied to the grid's filter model.
 */
export function useFilterGroups(): UseFilterGroupsResult {
  const apiRef = useGridApiContext();
  const filterModel: IssueFilterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterableColumns = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector);

  // Kept once found, so the row does not change while the columns load.
  const startingRowRef = useRef<GridFilterItem>();
  startingRowRef.current ??= createFilterRow(filterableColumns);
  const startingRow = startingRowRef.current;
  const shownModel = useMemo(
    () => simplifyFilterGroups(withStartingRow(filterModel, startingRow)),
    [filterModel, startingRow]
  );
  const nodes = useMemo(() => listFilterPanelNodes(shownModel), [shownModel]);

  /**
   * Applies a change to the model the panel shows, simplified the way it is
   * drawn; the reason tells the grid it was the user's (e.g. back to page 1).
   */
  const changeModel = useCallback(
    (change: (model: IssueFilterModel) => IssueFilterModel, reason: GridControlledStateReasonLookup['filter']) =>
      apiRef.current.setFilterModel(simplifyFilterGroups(change(shownModel)), reason),
    [apiRef, shownModel]
  );

  const addRow = useCallback(
    () => {
      const row = createFilterRow(filterableColumns);
      if (row) {
        changeModel((model) => addFilterRow(model, row), 'upsertFilterItems');
      }
    },
    [changeModel, filterableColumns]
  );

  const addGroup = useCallback(() => {
    const firstRows = [createFilterRow(filterableColumns), createFilterRow(filterableColumns)].filter(isDefinedValue);
    if (firstRows.length) {
      changeModel(
        (model) => addFilterGroup(model, crypto.randomUUID(), firstRows, crypto.randomUUID()),
        'upsertFilterItems'
      );
    }
  }, [changeModel, filterableColumns]);

  return useMemo(
    () => ({
      nodes,
      logicOperator: shownModel.logicOperator ?? GridLogicOperator.And,
      hasRows: filterModel.items.length > 0,
      changeRow: (row) => changeModel((model) => upsertFilterRow(model, row), 'upsertFilterItem'),
      removeRow: (row) => changeModel((model) => removeFilterRow(model, row.id), 'deleteFilterItem'),
      addRow,
      addGroup,
      changeLogicOperator: (logicOperator) =>
        changeModel((model) => setFilterLogicOperator(model, logicOperator), 'changeLogicOperator'),
      changeGroupLogicOperator: (groupId, logicOperator) =>
        changeModel((model) => setFilterGroupLogicOperator(model, groupId, logicOperator), 'changeLogicOperator'),
      removeAll: () =>
        changeModel(
          (model) => ({ ...model, items: [], ...(model.groupLogicOperators && { groupLogicOperators: {} }) }),
          'removeAllFilterItems'
        ),
    }),
    [nodes, shownModel, filterModel, changeModel, addRow, addGroup]
  );
}
